import Emitter from "es6-event-emitter";
import APIManager from "./../utilities/apimanager";
import { createRedirectionLogoutUrl } from "./../utilities/utils";
import { LOGOUT_REDIRECTION } from "./../utilities/redirectorKeys";
import {
  clearReaderSession,
  isUserNotLoggedInButHasContentAccess,
} from "../services/readerSession";
import CookieManager from "../utilities/cookiemanager";
// this class will implement al the necessary API communication
// and cookie management to authenticate users and authorize
// downloads. It does not implement any UI elements.
export default class SubScriptionManagerBase extends Emitter {
  _appState = null;
  _sessionManager = null;
  _cookieManager = null;
  _apiManager = null;
  _options = null;
  _subscriptionId = undefined;

  constructor(
    appState,
    sessionManager,
    apiManager,
    cookieManager,
    options = {}
  ) {
    super();
    this._appState = appState;
    this._sessionManager = sessionManager;
    this._apiManager = apiManager;
    this._cookieManager = cookieManager;
    this._options = options;
    // check the cookie for authentication and set the status.

    //set UUID
    this._appState.uuid = this._sessionManager.UUID;
  }

  // Returns the current authentication state.
  get isAuthenticated() {
    return !!this._cookieManager._getLoginCookie();
  }

  static get subscriptionReference() {
    const cookie = CookieManager._getLoginCookie();
    return cookie ? cookie.subscriptionReference : null;
  }

  static get subscriptionUserReference() {
    const cookie = CookieManager._getLoginCookie();
    return cookie ? cookie.subscriptionUserReference : null;
  }

  static get userSubscriptionType() {
    const cookie = CookieManager._getLoginCookie();
    return cookie ? cookie.userSubscriptionType : null;
  }

  // returns a promise that will either reject with an error code when authentication
  // fails, either resolves to success
  // in any case it will also trigger the authenticationstatuschanged event.
  authenticateByUserNamePassword(username, password) {
    const authenticationPromise = new Promise((resolve, reject) => {
      reject("not implemented", username, password);
    });
    return authenticationPromise;
  }

  // returns a promise that will either reject with an error code when authentication
  // fails, either resolves to success. This is to be used in case of SSO, or
  // to check if you are autenticated
  // in any case it will also trigger the authenticationstatuschanged event if
  // the current status changed.
  // eslint-disable-next-line
  authenticate(downloadToken) {
    const authenticationPromise = new Promise((resolve, reject) => {
      // check if there is an SSO cookie if the settings are SSO enabled,
      // else check for the subscription cookie.
      reject("not implemented", downloadToken);
    });
    return authenticationPromise;
  }

  authorizeArticle(contentPackageId, publicationId, pageId, contentItemId) {
    if (this.isAuthenticated) {
      return true;
    } else {
      if (isUserNotLoggedInButHasContentAccess()) {
        return true;
      }
      if (this._appState.socialSharing) {
        if (this._appState.socialSharingReset === "session") {
          if (this._appState.viewedArticles.indexOf(contentItemId) >= 0) {
            return true;
          } else if (
            this._appState.viewedArticles.length <
            this._appState.socialSharingNumberOfArticles
          ) {
            this._appState.viewedArticles.push(contentItemId);
            return true;
          } else if (
            this._appState.viewedArticles.length ==
            this._appState.socialSharingNumberOfArticles
          ) {
            if (parent) {
              parent.postMessage(
                {
                  MessageName: "SHARING_LIMIT_REACHED",
                  SharingFreemiumLimit:
                    this._appState.socialSharingNumberOfArticles,
                  SocialSharingReset: this._appState.socialSharingReset,
                  Url: window.location.href,
                },
                "*"
              );
            }
            return false;
          } else {
            return false;
          }
        } else if (this._appState.socialSharingReset === "contentpackage") {
          if (
            typeof Storage !== "undefined" &&
            this._appState.socialSharingNumberOfArticles > 0
          ) {
            let item = JSON.parse(
              localStorage.getItem(contentPackageId.toString())
            );
            if (!item) {
              item = {};
            }
            if (item[contentItemId.toString()]) {
              return true;
            } else if (
              Object.keys(item).length <
              this._appState.socialSharingNumberOfArticles
            ) {
              item[contentItemId.toString()] = true;
              localStorage.setItem(
                contentPackageId.toString(),
                JSON.stringify(item)
              );
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        } else if (this._appState.socialSharingReset === "week") {
          if (
            typeof Storage !== "undefined" &&
            this._appState.socialSharingNumberOfArticles > 0
          ) {
            let item = JSON.parse(localStorage.getItem("contentItemsWeek"));
            if (item && item["items"]) {
              const items = item["items"];
              if (items[contentItemId.toString()]) {
                return true;
              } else if (
                Object.keys(items).length <
                this._appState.socialSharingNumberOfArticles
              ) {
                items[contentItemId.toString()] = true;
                localStorage.setItem("contentItemsWeek", JSON.stringify(item));
                return true;
              } else {
                return false;
              }
            }
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
  }

  // returns a promise that will either reject with an error code when authorization
  // fails, either resolves to success. It will tagke as the assumption that
  // the CP and Publication is authorized and only uses the pageId for freemium
  // and metered model implementations
  // eslint-disable-next-line
  authorizePage(contentPackageId, publicationId, pageId) {
    if (
      this._appState.isNextgenSharing() &&
      !this.authorizeArticle(
        contentPackageId,
        publicationId,
        pageId,
        `${contentPackageId}_${pageId}`
      )
    ) {
      return Promise.reject(false);
    }
    return Promise.resolve(true);
  }

  // delete the subscriptionCookie and fire event if authenticationstatuschanged
  logOut() {
    const promise = new Promise((resolve, reject) => {
      if (this._options.paywallRedirectionUrl) {
        APIManager.getUserProfileValue(LOGOUT_REDIRECTION)
          .then((result) => {
            this.removeAuthenticationCookies();
            if (result && result[0] && result[0].Value) {
              window.top.location.href = createRedirectionLogoutUrl(
                result[0].Value
              );
            } else {
              this.removeAuthenticationCookies();
              reject();
            }
            resolve();
          })
          .catch(() => {
            this.removeAuthenticationCookies();
            reject();
          });
      } else {
        this.removeAuthenticationCookies();
      }
    });
    return promise;
  }

  removeAuthenticationCookies() {
    if (this.isAuthenticated) {
      this._cookieManager.removeCookie(
        this._cookieManager.cookieKeys.REDIRECTIONS
      );
      this._deleteSubscriptionCookie();
      clearReaderSession();
      this.trigger("subscriptionmanager:authenticationstatuschanged");
    }
  }

  _deleteSubscriptionCookie() {
    this._cookieManager.removeCookie(this._cookieManager.cookieKeys.LOGIN);
    this._appState.loginInformation = null;
  }

  _updateAppStateUserInfo(loginCookie) {
    if (loginCookie) {
      const {
        subscriptionReference,
        subscriptionUserReference,
        userSubscriptionType,
      } = loginCookie;
      this._appState.setUserInfo({
        subscriptionReference,
        subscriptionUserReference,
        userSubscriptionType,
      });
    }
  }

  _createSubscriptionCookie(
    subsId,
    subscriptionReference,
    subscriptionUserReference,
    userSubscriptionType,
    userName
  ) {
    this._updateAppStateUserInfo({
      subscriptionReference,
      subscriptionUserReference,
      userSubscriptionType,
    });
    const info = {
      subsId,
      subscriptionReference,
      subscriptionUserReference,
      userSubscriptionType,
    };
    if (userName) {
      info.userName = userName;
    }

    this._cookieManager.createCookie(
      this._cookieManager.cookieKeys.LOGIN,
      JSON.stringify(info)
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/subscriptionmanagement/subscriptionmanagerbase.js